/**
 * @file CriticalAlert.component.tsx
 * @module app/modules/price-catalog/components/CriticalAlert.component
 * @description Critical alert notification component for price catalog.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Table,
  Spinner,
  Row,
  Col,
} from "react-bootstrap";
import APICallService from "../../../../api/apiCallService";
import { manageProduct } from "../../../../api/apiEndPoints";
import { Response } from "../../../../types/response_data";
import { PriceCatalog } from "../../../../types";
import { InfiniteScroll } from "../../../components";
import { CustomSelect } from "../../../custom/Select/CustomSelect";
import { useWarehouse } from "../../../context/WarehouseContext";
import { CriticalAlertFilterOption } from "../../../../types/price_catalog";


const ALERTS_PAGE_LIMIT = 10;

interface CriticalAlertsTabProps {
  // Pre-selects the product/variant filter, e.g. when arriving from a critical alert badge elsewhere in the UI.
  initialProductFilter?: { value: string; label: string } | null;
}

export const CriticalAlertsTab: React.FC<CriticalAlertsTabProps> = ({
  initialProductFilter,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [records, setRecords] = useState<PriceCatalog[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  // null represents "All Tiers", otherwise 1, 2, or 3
  const [selectedTier, setSelectedTier] = useState<number | null>(null);

  // Product filter selection
  const [selectedProduct, setSelectedProduct] = useState<any>(
    initialProductFilter || null,
  );
  const [filterOptions, setFilterOptions] = useState<
    CriticalAlertFilterOption[]
  >([]);

  // Track which product rows are expanded
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const { selectedWarehouse } = useWarehouse();

  // Fetch filter options whenever the warehouse changes
  useEffect(() => {
    fetchCriticalAlertsFilterDetails();
  }, [selectedWarehouse]);

  // Refetch from the first page whenever the tier or product filter changes
  useEffect(() => {
    fetchAlerts(selectedTier, 0, false);
  }, [selectedTier, selectedProduct]);

  const fetchCriticalAlertsFilterDetails = async () => {
    try {
      const apiService = new APICallService(
        manageProduct.getCriticalAlertsFilterDetails,
        selectedWarehouse?._id,
        "",
        "",
        "",
        "",
      );
      const response: Response<{ variants: CriticalAlertFilterOption[] }> =
        await apiService.callAPI();
      if (response.status === "success") {
        setFilterOptions(response?.data.variants || []);
      }
    } catch (error) {
      console.error("Failed to fetch critical alerts filter details", error);
      setFilterOptions([]);
    }
  };

  // Mirrors the row key derivation used when rendering, so fetch-time expansion stays in sync
  const getRowKey = (record: PriceCatalog, index: number) =>
    record.variant || String(index);

  const fetchAlerts = async (
    tier: number | null,
    skip: number,
    append: boolean,
  ) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const params: {
        limit: number;
        skip: number;
        tier: number;
        warehouse: string | undefined;
        variant?: string;
      } = {
        limit: ALERTS_PAGE_LIMIT,
        skip,
        tier: tier ?? 0,
        warehouse: selectedWarehouse?._id,
      };

      if (selectedProduct?.value) {
        params["variant"] = selectedProduct?.value;
      }
      const apiService = new APICallService(
        manageProduct.getPriceCatalogEntries,
        params,
        "",
        "",
        "",
        "",
      );
      const response: Response<{ records: PriceCatalog[]; count: number }> =
        await apiService.callAPI();
      if (response.status === "success") {
        const newRecords = response.data.records;
        const baseIndex = append ? records.length : 0;
        const filterApplied = tier !== null || !!selectedProduct?.value;

        setRecords((prev) => (append ? [...prev, ...newRecords] : newRecords));
        setTotalCount(response.data.count);

        // Auto-expand so alerts are immediately visible once a tier/product filter narrows the list
        if (filterApplied) {
          setExpandedRows((prev) => {
            const next = append ? new Set(prev) : new Set<string>();
            newRecords.forEach((record, idx) => {
              next.add(getRowKey(record, baseIndex + idx));
            });
            return next;
          });
        } else if (!append) {
          setExpandedRows(new Set());
        }
      }
    } catch (error) {
      console.error("Failed to fetch critical alerts", error);
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  };

  const hasMore = !loading && records.length < totalCount;

  const loadMoreAlerts = () => {
    if (loading || loadingMore || !hasMore) return;
    fetchAlerts(selectedTier, records.length, true);
  };

  // Color Coding Helper based on Tier
  const getTierConfig = (tier: number) => {
    switch (tier) {
      case 1:
        return { bg: "danger", label: "Tier 1", textClass: "text-danger" }; // High priority (Red)
      case 2:
        return { bg: "warning", label: "Tier 2", textClass: "text-warning" }; // Medium priority (Yellow)
      case 3:
        return { bg: "info", label: "Tier 3", textClass: "text-info" }; // Low priority (Blue/Info)
      default:
        return {
          bg: "secondary",
          label: `Tier ${tier}`,
          textClass: "text-muted",
        };
    }
  };

  // Toggles the expansion state of a specific row
  const toggleRow = (rowKey: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  };

  return (
    <Card className="border bg-f9f9f9 py-3 custom-tabContainer">
      <Card.Header className="border-bottom-0 mb-3 d-flex justify-content-between align-items-center">
        <Card.Title className="fs-22 fw-bolder mb-0">
          Critical Alerts
        </Card.Title>

        {/* Tier Filter Buttons */}
        {/** create a new component for this one */}
        <ButtonGroup size="sm" className="shadow-sm">
          <Button className="uw-btn uw-btn-primary"
            variant={selectedTier === null ? "dark" : "outline-dark"}
            onClick={() => setSelectedTier(null)}
          >
            All Tiers
          </Button>
          <Button className="uw-btn uw-btn-primary"
            variant={selectedTier === 1 ? "danger" : "outline-danger"}
            onClick={() => setSelectedTier(1)}
          >
            Tier 1
          </Button>
          <Button className="uw-btn uw-btn-primary"
            variant={selectedTier === 2 ? "warning" : "outline-warning"}
            onClick={() => setSelectedTier(2)}
          >
            Tier 2
          </Button>
          <Button className="uw-btn uw-btn-primary"
            variant={selectedTier === 3 ? "info" : "outline-info"}
            onClick={() => setSelectedTier(3)}
          >
            Tier 3
          </Button>
        </ButtonGroup>
      </Card.Header>

      <Card.Body className="p-0 pb-6">
        {/* Filters Section */}
        <Card className="border-0 shadow-sm mb-5 mx-6">
          <Card.Body className="px-7 py-6">
            <Row className="align-items-end gap-5">
              <Col xs={12} sm={12} md={3}>
                <div className="w-100">
                  <label className="form-label fw-bold mb-2">Product</label>
                  <CustomSelect
                    backgroundColor="#ffffff"
                    placeholder="Select product"
                    isSearchable={true}
                    isClearable={true}
                    options={filterOptions.map((option) => ({
                      value: option._id,
                      label: option.title,
                    }))}
                    value={selectedProduct}
                    onChange={(option: any) => setSelectedProduct(option)}
                  />
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <div className="table-responsive h-lg-500px overflow-y-auto border-top border-bottom bg-white">
          <InfiniteScroll
            hasMore={hasMore}
            loading={loadingMore}
            onLoadMore={loadMoreAlerts}
          >
            <Table className="table table-row-bordered align-middle gs-7 gy-4 mb-0">
              <thead className="sticky-top bg-light">
                <tr className="fs-15 fw-bold text-muted text-uppercase h-60px align-middle">
                  <th className="min-w-250px ps-4">Product / Variant</th>
                  <th className="min-w-100px">Tier</th>
                  <th className="min-w-300px">Alert Message</th>
                  <th className="min-w-150px text-end pe-4">Date Logged</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                    </td>
                  </tr>
                ) : records.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center py-5 text-muted fw-500"
                    >
                      No critical alerts found for this selection.
                    </td>
                  </tr>
                ) : (
                  records.map((record, index) => {
                    const rowKey = getRowKey(record, index);
                    const isExpanded = expandedRows.has(rowKey);

                    return (
                      <React.Fragment key={rowKey}>
                        {/* 1. Product Group Header Row (Clickable) */}
                        <tr
                          className="bg-light bg-opacity-50"
                          onClick={() => toggleRow(rowKey)}
                          style={{ cursor: "pointer" }}
                        >
                          <td colSpan={4} className="ps-4">
                            <div className="d-flex align-items-center gap-3">
                              <i
                                className={`bi bi-chevron-${
                                  isExpanded ? "up" : "down"
                                } text-muted fw-bold`}
                              ></i>
                              <span className="fw-bolder text-dark fs-15">
                                {record.title}
                              </span>
                              <span className="badge bg-secondary bg-opacity-10 text-dark border">
                                {record.criticalAlerts.length} Alert
                                {record.criticalAlerts.length > 1 ? "s" : ""}
                              </span>
                            </div>
                          </td>
                        </tr>

                        {/* 2. Individual Alerts (Visible only if row is expanded), listed tier-wise (Tier 1 first) */}
                        {isExpanded &&
                          [...record.criticalAlerts]
                            .sort((a, b) => a.tier - b.tier)
                            .map((alert, alertIdx, sortedAlerts) => {
                              const tierConfig = getTierConfig(alert.tier);
                              const isNewTierGroup =
                                alertIdx === 0 ||
                                sortedAlerts[alertIdx - 1].tier !== alert.tier;

                              return (
                                <React.Fragment key={alert._id}>
                                  {isNewTierGroup && (
                                    <tr className="bg-light bg-opacity-25">
                                      <td
                                        colSpan={4}
                                        className="ps-10 py-2 fs-13 fw-bolder text-uppercase"
                                      >
                                        <span className={tierConfig.textClass}>
                                          {tierConfig.label}
                                        </span>
                                      </td>
                                    </tr>
                                  )}
                                  <tr>
                                    <td className="ps-10 text-muted fs-13">
                                      <i className="bi bi-arrow-return-right me-2"></i>
                                    </td>
                                    <td>
                                      <Badge
                                        bg={tierConfig.bg}
                                        className="px-3 py-2 fs-13 shadow-sm text-white"
                                      >
                                        {tierConfig.label}
                                      </Badge>
                                    </td>
                                    <td
                                      className={`fs-14 fw-500 ${tierConfig.textClass}`}
                                    >
                                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                      {alert.message}
                                    </td>
                                    <td className="text-end pe-4 fs-14 fw-500 text-muted">
                                      {new Date(alert.createdAt).toLocaleString(
                                        undefined,
                                        {
                                          year: "numeric",
                                          month: "short",
                                          day: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        },
                                      )}
                                    </td>
                                  </tr>
                                </React.Fragment>
                              );
                            })}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </InfiniteScroll>
        </div>
      </Card.Body>
    </Card>
  );
};
